/* eslint-disable max-lines */
import {
  ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation, Inject, Output, EventEmitter,
  ChangeDetectorRef, OnDestroy, Input, HostListener, OnChanges, SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription, BehaviorSubject, iif, of, } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { filter, map, take, skipWhile, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { format } from 'date-fns';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import { TranslocoService } from '@jsverse/transloco';

import { isNullOrUndefined } from '@app/util/util';
import { environment as ENV } from '@environment';
import { SearchType } from '@search-bar/entities/search-type';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { AppFacade } from '@app/app.facade';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { ToastrTypes } from '@app/entities/toastr';
import { hotelResultsRootRoute } from '@hotels/hotel-results/hotel-results-routing.module';
import { hotelDetailRootRoute } from '@hotels/hotel-detail/hotel-detail-routing.module';
import { HotelDetailResponse } from '@hotels/hotel-detail/entities/hotel-detail.response';
import { HotelsSearchAbstractComponent } from '@hotels/hotel-search/abstracts/hotels-search.abstract';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { blockSearchByDates, isNationalSearch, canSearch } from '@ux/mappers/date.mapper';
import { RappiTravelModules } from '@ux/entities/modules';
import { validChildrenAges } from '@ux/mappers/guest.mapper';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { SALES_CHANNEL } from '@app/modules/core/constants/query-params';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { SessionStorageService } from '@app/services/session-storage.service';
import { AvoService } from '@avo/services/avo.service';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { ButtonSize } from '@design-system/components/molecules/button/entities/button.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { HotelDestinationInputType } from '@shared/base-search-bar/entities/autocomplete-destionation';

@Component({
  selector: 'app-hotel-search',
  templateUrl: './hotel-search.component.html',
  styleUrls: ['./hotel-search.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelSearchComponent extends HotelsSearchAbstractComponent implements OnInit, OnDestroy, OnChanges {

  public rooms: Room[] = [];
  
  public roomControl: UntypedFormControl = new UntypedFormControl();
  public arrival: PlaceType = PlaceType.ARRIVAL;
  public eventPath: BehaviorSubject<EventTarget[]> = new BehaviorSubject<EventTarget[]>([]);

  public readonly icons = Icon;
  
  private subscriptions: Subscription[] = [];
  private childrenAgesInfo: string = 'SEARCH_MODULE.ROOMS.CHILDREN_AGES_INFO';

  @Input() showSearch: boolean = false;
  @Input() loadSearchData: boolean = false;
  @Input() isResults: boolean = false;
  @Input() hotelDetail: HotelDetailResponse;
  @Input() isMobile: boolean;
  @Input() loadDefaultplaces: boolean;

  @Output() showDestination: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() redirectToSearch: EventEmitter<void> = new EventEmitter<void>();
  @Output() showRoomsComponent: EventEmitter<boolean> = new EventEmitter<boolean>();

  @HostListener('document:click', ['$event']) clickout(event: Event): void {
    if (this.isBrowser) this.eventPath.next(event.composedPath());
  }

  constructor(
    protected baseSearchbarfacade: BaseSearchBarFacade,
    protected avoService: AvoService,
    protected amplitude: PropertiesAmplitudeService,
    private router: Router,
    private translocoService: TranslocoService,
    private appFacade: AppFacade,
    private cdr: ChangeDetectorRef,
    private translateService: TranslocoService,
    private triggerAmplitudeService: TriggerAmplitudeService,
    private sessionStorageService: SessionStorageService,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super(baseSearchbarfacade, avoService, amplitude);
    this.loadDefaultplaces = true;
    this.searchType = SearchType.HOME_HOTELS;
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      if (!this.isResults && !this.hotelDetail && this.loadDefaultplaces) {
        this.baseSearchbarfacade.loadSearchData(SearchBy.HOTELS);
      }
      this.subscriptions.push(
        this.baseSearchbarfacade.searchData$
          .pipe(
            filter(data => !!data),
            map(searchData => ({ searchData })),
            switchMap(({ searchData }) => iif(
              () => !!searchData.guestsPerRoom,
              this.roomInputLabel$(searchData.guestsPerRoom).pipe(
                tap(label => {
                  this.setRoomsData(searchData.guestsPerRoom, label);
                })
              ),
              of(null)
            ).pipe(
              tap(() => {
                this.placeSelectedItem = searchData.hotelDestinationPlace;
                this.cdr.detectChanges();
                this.cdr.markForCheck();
              })
            ))
          ).subscribe(),
        this.baseSearchbarfacade.searchData$.pipe(
          skipWhile(data => isEmpty(data)),
          distinctUntilChanged((current: SearchData, preview: SearchData) => this.areHotelDatesEqual(current, preview))
        ).subscribe(data => {
          this.departureDate = data.departureDate;
          this.arrivalDate = data.arrivalDate;
          this.cdr.detectChanges(); this.cdr.markForCheck();
        }));
      if (this.searchType === SearchType.HOME_HOTELS) this.registerViewOrSelectAmplitudeEvent(this.source);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showSearch && changes.showSearch.currentValue) {
      const source = (this.source === EVENT_SOURCE.RESULTS && this.showHotelDetail ? EVENT_SOURCE.DETAIL : this.source);
      this.registerViewOrSelectAmplitudeEvent(source);
    }
  }

  ngOnDestroy(): void {
    this.cdr.detach(); this.subscriptions.forEach(s => s.unsubscribe());
  }

  get buttonSize(): ButtonSize {
    return this.isResults || this.isHotelDetail ? ButtonSize.SquareSM : ButtonSize.SquareRG;
  }

  get isHotelDetail(): boolean {
    return this.searchType === SearchType.HOTELS_DETAIL;
  }

  get searchButtonLabel(): string {
    return this.isHotelDetail ? 'BUTTONS.EDIT' : 'BUTTONS.SEARCH';
  }

  get hotelSearchHome(): boolean {
    return this.searchType === SearchType.HOME_HOTELS;
  }

  get mappedRooms(): string {
    return !isNullOrUndefined(this.rooms) ? this.rooms.map(item =>
      `${item[TravelerTypes.ADULTS]}_${item[TravelerTypes.CHILDREN]}_${item.childrenAges.join('_')}`).join('-') : null;
  }

  get searchValid(): boolean {
    const validatePlace = this.isHotelDetail ? true : !this.isPlaceSelectedEmpty;
    return validatePlace && !isNullOrUndefined(this.departureDate) && !isNullOrUndefined(this.arrivalDate) &&
      !isNullOrUndefined(this.rooms) && validChildrenAges(this.rooms);
  }

  public roomInputLabel$(rooms: Room[]): Observable<string> {
    return iif(
      () => rooms.length > 1,
      this.translocoService.selectTranslate('HOTELS_MODULE.HOME.ROOMS_SINGLE'),
      this.translocoService.selectTranslate('HOTELS_MODULE.HOME.ROOM')
    ).pipe(
      map(roomsLabel => {
        let countGuests = 0;
        rooms.forEach(data => {
          countGuests += data[TravelerTypes.ADULTS];
          countGuests += data[TravelerTypes.CHILDREN];
        });
        return { roomsLabel: this.toLowerCaseFirstLetter(roomsLabel), countGuests };
      }),
      switchMap(({ roomsLabel, countGuests }) => iif(
        () => countGuests > 1,
        this.translocoService.selectTranslate('HOTELS_MODULE.HOME.GUESTS'),
        this.translocoService.selectTranslate('HOTELS_MODULE.HOME.GUEST')
      ).pipe(
        map(guestLabel => `${rooms.length} ${roomsLabel}, ${countGuests} ${this.toLowerCaseFirstLetter(guestLabel)}`)
      ))
    );
  }

  public showSearchBarRoom(): void {
    this.showRoomsComponent.emit();
  }

  public navigateToSearch(): void {
    this.isSubmited = true;
    if (this.searchValid) {
      this.baseSearchbarfacade.searchData$.pipe(
        take(1),
        tap(data => {
          if (!canSearch(RappiTravelModules.hotels) && blockSearchByDates(data.destinationPlace, data.departureDate,
            ENV.validate_search.hotels)) {
            this.isSubmited = false;
            const messageType = isNationalSearch(data.destinationPlace) ? 'HOTEL_NATIONAL' : 'HOTEL_INTERNATIONAL';
            this.appFacade.fetchToastrMessage({
              type: ToastrTypes.ERROR,
              message: this.translateService.translate(`BLOCK_SEARCH_MESSAGE.${messageType}`)
            });
          } else {
            this.baseSearchbarfacade.saveSearchData({ ...data, guestsPerRoom: this.rooms }, SearchBy.HOTELS);
            this.search();
          }
          if (this.showHotelDetail) this.redirectToSearch.emit();
        })
      ).subscribe().unsubscribe();
    }
  }

  public roomsSelection(rooms: Room[]): void {
    this.roomInputLabel$(rooms).pipe(
      tap(label => {
        this.setRoomsData(rooms, label);
      })
    ).subscribe().unsubscribe();
  }

  public toLowerCaseFirstLetter(value: string): string {
    return value.charAt(0).toLowerCase() + value.slice(1);
  }
  public hideCalendarInput(): void {
    this.showCalendarInput = false;
  }

  public applyCalendar(calendar: Date[]): void {
    this.departureDate = calendar?.[0];
    this.arrivalDate = calendar?.[1];
    this.baseSearchbarfacade.selectCalendarDates(calendar);
    this.hideCalendarInput();
  }

  public fetchMessageAction(): void {
    this.appFacade.fetchToastrMessage({
      message: this.translocoService.translate(this.childrenAgesInfo), type: ToastrTypes.ERROR
    });
  }

  public setGuestsPerRoom(event: Room): void {
    this.baseSearchbarfacade.setGuestsPerRoom(event);
  }

  public addRoom(event: Room): void {
    this.baseSearchbarfacade.addNewRoom(event);
  }

  public removeRoom(event: number): void {
    this.baseSearchbarfacade.deleteRoom(event);
  }

  private setRoomsData(rooms: Room[], value: string): void {
    this.rooms = rooms;
    this.roomControl = new UntypedFormControl(value);
  }

  private search(): void {
    const _departureDate = format(this.departureDate, 'yyyy-MM-dd');
    const _arrivalDate = format(this.arrivalDate, 'yyyy-MM-dd');
    if (!this.isHotelDetail) {
      this.baseSearchbarfacade.searchData$.pipe(
        take(1),
        tap(() => {
          this.redirectToSearch.emit();
          this.sessionStorageService.setEncodeDestinationType(this.placeSelectedItem?.InputType);
          this.sessionStorageService.setEncodeDestinationId(this.placeSelectedItem?.Id);
          this.sessionStorageService.setEncodeDestinationIATA(this.placeSelectedItem?.NearestIata);
        }),
        switchMap(data => this.amplitude.searchProperties$(SearchBy.HOTELS).pipe(
          take(1), map(searchProps => ({
            _source: this.showHotelDetail ? EVENT_SOURCE.DETAIL : this.source, searchProps
          })),
          switchMap(({ searchProps, _source }) => this.triggerAmplitudeService.trackAmplitudeEventProps$(
            AVO_EVENTS.SELECT_TRAVEL_SEARCH, { SOURCE: _source }, searchProps
          ).pipe(
            tap(trigger => {
              triggerAmplitudeEventApp(trigger);
            })
          )),
          tap(() => {
            this.router.navigate(
              [this.getUrlRedirectByResults(_departureDate, _arrivalDate)],
              { queryParams: { source: this.source, branch_code: data.branchCode, [SALES_CHANNEL]: data.salesChannel } });
          })
        )),
      ).subscribe().unsubscribe();
    } else if (this.hotelDetail && this.hotelDetail.Result) {
      this.registerViewOrSelectAmplitudeEvent(this.source, true);
      this.router.navigate([
        `/${hotelDetailRootRoute}/${this.hotelDetail.Result.HotelId}/${this.hotelDetail.Result.Destination}` +
        `/${_departureDate}/${_arrivalDate}/${this.mappedRooms}/${ENV.countryInfo.currency_code}`
      ], { queryParams: { source: isNullOrUndefined(this.source) ? EVENT_SOURCE.DETAIL : this.source } });
    }
  }

  private getUrlRedirectByResults(_departureDate: string, _arrivalDate: string): string {
    if (this.placeSelectedItem?.isPopularSelection) {
      return `/${hotelResultsRootRoute}/${HotelDestinationInputType.SearchBar}/${this.placeSelectedItem?.NearestIata}` +
        `/${_departureDate}/${_arrivalDate}/${ENV.countryInfo.currency_code}/${this.mappedRooms}`;
    } else {
      return `/${hotelResultsRootRoute}/${this.placeSelectedItem?.InputType}/${this.placeSelectedItem?.Id}` +
        `/${_departureDate}/${_arrivalDate}/${ENV.countryInfo.currency_code}/${this.mappedRooms}`;
    }
  }

  private areHotelDatesEqual(current: SearchData, preview: SearchData): boolean {
    return isEqual(current.arrivalDate, preview.arrivalDate) &&
    isEqual(current.departureDate, preview.departureDate);
  }
}
